/**
 * useCourse: everything the course player loads before it can show the
 * first stage (plan docs/plans/lms-plan.md 7.2, 8.1, 18.15, 18.17; WP-40).
 *
 *   1. GET /courses/:id (the overview) and GET /me/assignments?status=open
 *      (the caller's open assignment for this course, if any), together;
 *   2. the version to train on: the assignment's pinned version
 *      (required_version_id, a retrain or acknowledge assignment, 8.1), else
 *      the course's current version;
 *   3. GET /courses/:id/content (with ?version_id= when that is not the
 *      current version, 18.17) and GET /me/progress/:courseId?version_id=,
 *      together.
 *
 * The load re-runs when the course id or the site changes (every /api/lms
 * call carries X-Site-Id) and on reload(); an answer for a request that is no
 * longer the latest is dropped. Any failure fails the whole load with a
 * message and a Retry (G10: failures are loud; the player never guesses the
 * version it trains on).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLms } from '../LmsProvider';
import { apiErrorMessage } from '../api/client';
import { getCourse, getCourseContent, getMyOpenAssignment } from '../api/courses';
import type { CourseDetail, MyAssignment } from '../api/courses';
import { getMyProgress } from '../api/progress';
import type { LmsProgress } from '../api/progress';
import type { CourseContent, QuizSettings } from '../content/types';

export interface CourseData {
  course: CourseDetail;
  /** The caller's open assignment for the course, or null. */
  assignment: MyAssignment | null;
  /** The version the player trains on. */
  version: { id: string; version_no: number };
  content: CourseContent;
  /** The version's own settings; {} for a read_ack version (5.2). */
  quizSettings: Partial<QuizSettings>;
  progress: LmsProgress | null;
}

export interface UseCourseResult {
  data: CourseData | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface HttpError {
  response?: { status?: number };
}

function loadError(err: unknown): string {
  if ((err as HttpError | null)?.response?.status === 404) {
    return "This course was not found, or it has no published version you can take.";
  }
  return apiErrorMessage(err, 'The course could not be loaded.');
}

export function useCourse(courseId: string | undefined): UseCourseResult {
  const { siteId } = useLms();
  const [data, setData] = useState<CourseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = requestRef.current + 1;
    requestRef.current = request;
    const current = () => requestRef.current === request;

    if (!courseId || !siteId) {
      setData(null);
      setLoading(false);
      setError(courseId ? 'Select a site to take this course.' : 'No course was named.');
      return undefined;
    }

    setLoading(true);
    setError(null);
    setData(null);

    (async () => {
      try {
        const [course, assignment] = await Promise.all([getCourse(courseId), getMyOpenAssignment(courseId)]);
        if (!current()) return;
        const versionId = assignment?.required_version_id || course.current_version?.id || null;
        if (!versionId) {
          setError('This course has no published version yet.');
          setLoading(false);
          return;
        }
        const pinned = versionId !== course.current_version?.id;
        const [contentResponse, progress] = await Promise.all([
          getCourseContent(courseId, pinned ? versionId : null),
          getMyProgress(courseId, versionId),
        ]);
        if (!current()) return;
        const content = contentResponse.content;
        if (!content || !Array.isArray(content.sections)) {
          setError('This course version has no content to show.');
          setLoading(false);
          return;
        }
        setData({
          course,
          assignment,
          version: contentResponse.version,
          content,
          quizSettings: (contentResponse.quiz_settings || {}) as Partial<QuizSettings>,
          progress: progress && progress.course_version_id === contentResponse.version.id ? progress : null,
        });
        setLoading(false);
      } catch (err) {
        if (!current()) return;
        setError(loadError(err));
        setLoading(false);
      }
    })();

    return () => {
      // A newer request (or the unmount) makes this one stale.
      if (requestRef.current === request) requestRef.current = request + 1;
    };
  }, [courseId, siteId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { data, loading, error, reload };
}

export default useCourse;
